<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, sameAs } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentsAPI from 'dashboard/api/agents';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
  agentName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();

const password = ref('');
const passwordConfirmation = ref('');
const isLoading = ref(false);
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);
const serverErrors = ref([]);

const rules = {
  password: { 
    required, 
    minLength: minLength(6) 
  },
  passwordConfirmation: { 
    required,
    sameAsPassword: sameAs(password)
  },
};

const v$ = useVuelidate(rules, {
  password,
  passwordConfirmation,
});

const modalTitle = computed(() => 
  `${t('AGENT_MGMT.EDIT.PASSWORD_SET.TITLE')} - ${props.agentName}`
);

const parseServerErrors = (errorMessage) => {
  // Se já vier como array de erros do backend, retorna direto
  if (Array.isArray(errorMessage)) {
    return errorMessage;
  }
  
  // Divide por vírgula e limpa cada mensagem
  const errors = errorMessage.split(',').map(err => err.trim());
  
  // Remove duplicatas
  return [...new Set(errors)];
};

const setPassword = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isLoading.value = true;
  serverErrors.value = [];

  try {
    await AgentsAPI.setPassword(
      props.agentId,
      password.value,
      passwordConfirmation.value
    );
    
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_SET.SUCCESS_MESSAGE'));
    emit('success');
    emit('close');
  } catch (error) {
    const errorMessage = error?.response?.data?.error;
    
    if (errorMessage) {
      serverErrors.value = parseServerErrors(errorMessage);
    } else {
      serverErrors.value = [t('AGENT_MGMT.EDIT.PASSWORD_SET.ERROR_MESSAGE')];
    }
  } finally {
    isLoading.value = false;
  }
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const togglePasswordConfirmationVisibility = () => {
  showPasswordConfirmation.value = !showPasswordConfirmation.value;
};

const clearErrors = () => {
  serverErrors.value = [];
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="modalTitle" />
    <form class="w-full p-6" @submit.prevent="setPassword">
      
      <!-- Mensagens de erro do servidor -->
      <div
        v-if="serverErrors.length > 0"
        class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"
      >
        <div class="flex items-start">
          <i class="i-lucide-alert-circle text-red-600 text-xl mr-3 mt-0.5" />
          <div class="flex-1">
            <h4 class="text-sm font-semibold text-red-800 mb-2">
              {{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.REQUIREMENTS_TITLE') }}
            </h4>
            <ul class="list-disc list-inside space-y-1">
              <li
                v-for="(error, index) in serverErrors"
                :key="index"
                class="text-sm text-red-700"
              >
                {{ error }}
              </li>
            </ul>
          </div>
          <button
            type="button"
            class="text-red-400 hover:text-red-600"
            @click="clearErrors"
          >
            <i class="i-lucide-x text-lg" />
          </button>
        </div>
      </div>

      <!-- Informações sobre requisitos de senha -->
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div class="flex items-start">
          <i class="i-lucide-info text-blue-600 text-lg mr-2 mt-0.5" />
          <div class="text-sm text-blue-800">
            <p class="font-medium mb-1">{{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.REQUIREMENTS_TITLE') }}:</p>
            <ul class="list-disc list-inside space-y-0.5 text-xs">
              <li>Mínimo de 6 caracteres</li>
              <li>Pelo menos 1 letra maiúscula (A-Z)</li>
              <li>Pelo menos 1 caractere especial (!@#$%^&*...)</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="w-full mb-4">
        <label :class="{ error: v$.password.$error }">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_LABEL') }}
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_PLACEHOLDER')"
              class="w-full pr-10"
              @input="v$.password.$touch(); clearErrors()"
            />
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              @click="togglePasswordVisibility"
            >
              <i :class="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="text-lg" />
            </button>
          </div>
          <span v-if="v$.password.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full mb-6">
        <label :class="{ error: v$.passwordConfirmation.$error }">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_CONFIRMATION_LABEL') }}
          <div class="relative">
            <input
              v-model="passwordConfirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              :placeholder="$t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_CONFIRMATION_PLACEHOLDER')"
              class="w-full pr-10"
              @input="v$.passwordConfirmation.$touch(); clearErrors()"
            />
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              @click="togglePasswordConfirmationVisibility"
            >
              <i :class="showPasswordConfirmation ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="text-lg" />
            </button>
          </div>
          <span v-if="v$.passwordConfirmation.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_SET.PASSWORD_CONFIRMATION_ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex justify-end items-center gap-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
          :disabled="isLoading"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.EDIT.PASSWORD_SET.SUBMIT')"
          :disabled="v$.$invalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </form>
  </div>
</template>